import { useI18N } from "@sundae/react-hooks";
import { Button, toolkitCx } from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { FC, useMemo } from "react";

import AssetAmountBox from "../../../../components/AssetAmountBox";
import PoolSelectBox from "../../../../components/PoolSelectBox";
import { useZapTx } from "../../../../hooks/liquidity/useZapTx";
import { useAssetsWithBalance } from "../../../../hooks/useAssetsWithBalance";
import { Actions, useAppContext } from "../../../../stores";
import { useZapContext } from "../../../../stores/LiquidityContext/ZapContext/context";
import { EOrderTransactionBuilderState, useOrderContext } from "../../../../stores/OrderContext";
import { IAssetMetaData } from "../../../../types/Asset.types";
import { getAssetName } from "../../../../utils/assets.utils";
import ZapOutput from "../ZapOutput";
import ZapTransactionDetails from "../ZapTransactionDetails";

const ZapSetup: FC = () => {
  const { t } = useI18N("liquidity");
  const { assetsWithBalance, balanceLoaded } = useAssetsWithBalance();
  const {
    dispatch: appDispatch,
    state: { activeZapAsset, activeZapPool },
  } = useAppContext();
  const {
    derived: { zapAssetAmountExceedsBalance },
    handlers: { setZapInputValue },
    state: { liquidity, zapInputValue },
  } = useZapContext();
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();
  const { handleZap } = useZapTx();

  const assetList = useMemo(() => {
    const list: IMappedAsset<IAssetMetaData>[] = [];
    assetsWithBalance?.forEach((asset) => {
      const isAdaAsset = SundaeUtils.isAdaAsset(asset.metadata);
      const assetIds = isAdaAsset ? SundaeUtils.ADA_ASSET_IDS : [asset.metadata.assetId];
      if (
        assetIds.includes(activeZapPool?.assetA?.assetId ?? "") ||
        assetIds.includes(activeZapPool?.assetB?.assetId ?? "")
      ) {
        list.push(asset);
      }
    });

    return list;
  }, [activeZapPool, assetsWithBalance?.map(({ metadata }) => metadata.assetId)?.sort()]);

  const errorMessage = useMemo(() => {
    if (!activeZapAsset) return t("zap.error.missingAsset");
    if (zapInputValue === "0" || !zapInputValue) return t("zap.error.invalidValue");
    if (!activeZapPool) return t("zap.selectPool");
    return null;
  }, [activeZapPool, activeZapAsset, zapInputValue]);

  const buttonLabel = useMemo(() => {
    if (errorMessage) return errorMessage;
    if (transaction?.state === EOrderTransactionBuilderState.BUILD) {
      return t("building", { ns: "common" });
    }
    if (zapAssetAmountExceedsBalance && activeZapAsset) {
      return t("insufficientBalance", {
        asset: getAssetName(activeZapAsset.metadata),
        context: "asset",
        ns: "common",
      });
    }
    return t("zap.confirm.button", { ns: "liquidity" });
  }, [zapAssetAmountExceedsBalance, activeZapAsset?.metadata, errorMessage, transaction?.state]);

  return (
    <div className="grid h-fit gap-4">
      <AssetAmountBox
        activeAsset={activeZapAsset}
        assets={assetList}
        assetValue={zapInputValue}
        autoFocus
        dialogTitle={t("createPool.assetSelectDialogTitle")}
        setActiveAsset={(asset) => {
          Actions.setActiveZapAsset(asset, appDispatch);
        }}
        setValueAsset={setZapInputValue}
        topline={t("zap.assetAmountBox.topline")}
      />

      {activeZapAsset && balanceLoaded && <PoolSelectBox />}

      <div
        className={cx(
          "relative grid gap-4",
          toolkitCx.layout.spacing.horizontal.md,
          toolkitCx.layout.spacing.top.md
        )}
      >
        <div className="grid gap-6 motion-safe:animate-slide-up">
          <ZapOutput />

          {!errorMessage && liquidity && <ZapTransactionDetails />}

          <Button
            className="sticky bottom-4 z-10 w-full"
            disabled={
              zapAssetAmountExceedsBalance ||
              Boolean(errorMessage) ||
              !liquidity?.generatedLp ||
              transaction?.state === EOrderTransactionBuilderState.BUILD
            }
            onClick={() => handleZap()}
            variant="primary"
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ZapSetup;
